/** The record open beside QuantisAI (a bill, the programme, a document), as the screen that owns it registers it.
 *  Only ids, names and a short summary travel: the server reads the record itself through its own tools. */

export type SIRecordKind = 'boq' | 'programme' | 'document';

export type SIRecordContext = {
  /** Who registered it, so a screen only releases its own record. */
  key: string;
  kind: SIRecordKind;
  projectId: string;
  /** What the chip in the composer says, such as "Bill 2" or "Ground floor plan.pdf". */
  label: string;
  /** The bill (`boq`) or file (`document`) the screen has open. */
  recordId?: string | null;
  /** Rows the person has selected: BOQ items or programme activities. */
  selectedIds?: readonly string[];
  /** Plain-text lines for records QuantisAI has no tool to read (the programme). */
  summary?: readonly string[];
};

/** What `/si/chat` receives as `context.record`. */
export type SIRecordPayload = {
  kind: SIRecordKind;
  projectId: string;
  label: string;
  recordId?: string;
  selectedIds?: string[];
  summary?: string[];
};

export const RECORD_LIMITS = { label: 160, selected: 50, summaryLines: 25, summaryChars: 2000 } as const;

/** Trims a registered record to what the server accepts, or nothing when it belongs to another project. */
export function recordPayload(record: SIRecordContext | null, projectId: string | null | undefined): SIRecordPayload | undefined {
  if (!record || !projectId || record.projectId !== projectId) return undefined;
  const payload: SIRecordPayload = { kind: record.kind, projectId: record.projectId, label: record.label.trim().slice(0, RECORD_LIMITS.label) || 'Untitled' };
  if (record.recordId) payload.recordId = record.recordId;
  const selected = (record.selectedIds ?? []).filter(Boolean).slice(0, RECORD_LIMITS.selected);
  if (selected.length) payload.selectedIds = [...selected];
  const summary: string[] = [];
  let used = 0;
  for (const line of record.summary ?? []) {
    const text = line.trim().slice(0, 240);
    if (!text) continue;
    if (summary.length >= RECORD_LIMITS.summaryLines || used + text.length > RECORD_LIMITS.summaryChars) break;
    summary.push(text); used += text.length;
  }
  if (summary.length) payload.summary = summary;
  return payload;
}

/** The composer chip: "Bill 2, 3 items selected". */
export function recordChipText(record: SIRecordContext): string {
  const count = record.selectedIds?.length ?? 0;
  if (!count) return record.label;
  const noun = record.kind === 'programme'
    ? (count === 1 ? 'activity' : 'activities')
    : (count === 1 ? 'item' : 'items');
  return `${record.label}, ${count} ${noun} selected`;
}
